/**
 * CRM Multi-Compagnia · DryRunReport
 *
 * Mostra il report di simulazione: 4 card colorate + lista errori/warning.
 */
import { PlusCircle, RefreshCcw, AlertCircle, SkipForward } from "lucide-react";

function Card({ icon: Icon, label, value, color, testid }) {
  return (
    <div className={`p-4 rounded-lg border bg-${color}-50 border-${color}-200`} data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <Icon className={`w-4 h-4 text-${color}-600`} />
        <span className={`text-2xl font-bold text-${color}-700`}>{value}</span>
      </div>
      <p className={`text-xs text-${color}-700 uppercase tracking-wider`}>{label}</p>
    </div>
  );
}

export function DryRunReport({ report }) {
  if (!report) return null;
  const s = report.summary || {};
  return (
    <div className="space-y-4" data-testid="sia-dryrun-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card icon={PlusCircle} label="Da creare" value={s.rows_to_create ?? 0} color="emerald" testid="sia-dry-create" />
        <Card icon={RefreshCcw} label="Da aggiornare" value={s.rows_to_update ?? 0} color="blue" testid="sia-dry-update" />
        <Card icon={SkipForward} label="Duplicati / Skip" value={s.rows_duplicates_skipped ?? 0} color="amber" testid="sia-dry-skip" />
        <Card icon={AlertCircle} label="Errori" value={s.rows_with_errors ?? 0} color="rose" testid="sia-dry-errors" />
      </div>

      <p className="text-xs text-zinc-500">
        Totale righe analizzate: <strong>{s.rows_total ?? 0}</strong> · Tipo: <code>{report.tipo_archivio}</code>
      </p>

      {report.data_quality && (s.rows_with_errors > 0 || Object.values(report.data_quality).some(v => v > 0)) && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
          <p className="font-semibold text-amber-800 mb-1">Qualità dati rilevata:</p>
          <ul className="text-xs text-amber-700 space-y-0.5">
            {Object.entries(report.data_quality).map(([k, v]) => v > 0 && (
              <li key={k}>• {k.replace(/_/g, " ")}: <strong>{v}</strong></li>
            ))}
          </ul>
        </div>
      )}

      {(report.errors_sample || []).length > 0 && (
        <details className="bg-rose-50/50 border border-rose-200 rounded p-3" open>
          <summary className="text-sm font-medium text-rose-700 cursor-pointer">
            Primi {report.errors_sample.length} errori
          </summary>
          <ul className="mt-2 text-xs text-zinc-700 space-y-1 max-h-60 overflow-y-auto">
            {report.errors_sample.map((e, i) => (
              <li key={i} className="border-l-2 border-rose-300 pl-2">
                <strong>Riga {e.riga}:</strong> {e.errore || e.errore_msg || JSON.stringify(e)}
              </li>
            ))}
          </ul>
        </details>
      )}

      {(report.warnings_sample || []).length > 0 && (
        <details className="bg-amber-50/50 border border-amber-200 rounded p-3">
          <summary className="text-sm font-medium text-amber-700 cursor-pointer">
            Avvisi ({report.warnings_sample.length})
          </summary>
          <ul className="mt-2 text-xs text-zinc-700 space-y-1 max-h-40 overflow-y-auto">
            {report.warnings_sample.map((w, i) => (
              <li key={i}>Riga {w.riga}: {w.warning || (w.warnings || []).join(", ")}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
